import { useState } from "react";

/** Official card-list host whose scans are proxied through /img/card/*. */
const CARD_HOST = "en.palworld-official-cardgame.com";

/** Widths the /img/card proxy is allowed to emit (must match the route). */
const PROXY_WIDTHS = [140, 210, 280, 420, 640, 840];

/**
 * Rewrite an official card scan to our same-origin, immutably cached,
 * width-limited proxy. Any other URL is returned untouched.
 */
function proxied(u: string, w: number): string | undefined {
  if (!u.includes(CARD_HOST)) return undefined;
  try {
    const { pathname } = new URL(u);
    return `/img/card${pathname}?w=${w}`;
  } catch {
    return undefined;
  }
}

/**
 * Palworld TCG card image.
 *
 * Official card scans are served through the same-origin /img/card proxy so they
 * arrive as width-limited WebP with a one-year immutable cache lifetime instead
 * of ~100 KB uncached PNGs. Official Pal artwork keeps using the wiki CDN, which
 * already resizes and transcodes on request. Everything below the fold is lazy
 * loaded and decoded off the main thread.
 */
export function CardImage({
  src,
  alt,
  width,
  height,
  className = "",
  priority = false,
  responsive = false,
  sizes,
  draggable = false,
  onError,
  srcSet,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  priority?: boolean;
  /** Let CSS size the image (no inline width/height style). */
  responsive?: boolean;
  sizes?: string;
  /** Native image dragging — off by default so swipe/pan gestures work. */
  draggable?: boolean;
  onError?: () => void;
  /** Explicit candidate set; auto-derived for resizable official artwork URLs. */
  srcSet?: string;
}) {
  // If the proxy ever fails (upstream hiccup, edge cache miss error) we fall
  // straight back to the untouched official URL rather than showing a gap.
  const [bypassProxy, setBypassProxy] = useState(false);
  if (!src) return null;
  // Official Pal artwork is served from a CDN that resizes via
  // `/scale-to-width-down/<w>`, so we can ship real width descriptors and let
  // phones download a much smaller file than desktops. The same CDN also
  // transcodes on request, so every candidate asks for WebP explicitly instead
  // of relying on `Accept` negotiation (some proxies strip it and serve PNG).
  const modern = (u: string) =>
    /static\.wikia\.nocookie\.net/.test(u) && !/[?&]format=/.test(u)
      ? `${u}${u.includes("?") ? "&" : "?"}format=webp`
      : u;

  const target = Math.max(80, Math.round(width));
  const proxyBase = bypassProxy ? undefined : proxied(src, PROXY_WIDTHS.find((w) => w >= target) ?? 840);
  const url = proxyBase ?? modern(src);

  const candidates =
    srcSet ??
    (proxyBase
      ? // 1x / 2x candidates from the proxy so retina screens stay sharp while
        // ordinary displays download a fraction of the original PNG.
        [...new Set([PROXY_WIDTHS.find((w) => w >= target) ?? 840, PROXY_WIDTHS.find((w) => w >= target * 2) ?? 840])]
          .map((w) => `${proxied(src, w)} ${w}w`)
          .join(", ")
      : (() => {
          const m = /^(.*\/scale-to-width-down\/)(\d+)$/.exec(src);
          if (!m) return undefined;
          const base = m[1];
          const w = Number(m[2]);
          const widths = [...new Set([Math.round(w / 2), w, w * 2])].filter((x) => x >= 80 && x <= 1200);
          return widths.map((x) => `${modern(`${base}${x}`)} ${x}w`).join(", ");
        })());

  return (
    <img
      src={url}
      srcSet={candidates}
      alt={alt}
      width={Math.round(width)}
      height={Math.round(height)}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : "low"}
      sizes={sizes ?? (candidates ? `(max-width: 640px) 50vw, ${Math.round(width)}px` : undefined)}
      referrerPolicy="no-referrer"
      draggable={draggable}
      onError={() => {
        if (proxyBase) {
          setBypassProxy(true);
          return;
        }
        onError?.();
      }}
      style={responsive ? undefined : { width, height }}
      className={`object-contain bg-background/40 ${className}`}
    />
  );
}
